import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import socketIOClient from 'socket.io-client';
import {
	Card,
	CardContent,
	Grid,
	Typography,
	CircularProgress,
	Box
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { getDashboardMetrics, getUserPreferences, updateDashboardMetricsRealtime } from './store/actions/modernDashboard.actions';
import DashboardMetricsCards from './components/DashboardMetricsCards';
import MetricGauges from './components/MetricGauges';
import ClusterVisualization from './components/ClusterVisualization';
import PinnedApplicationsSection from './components/PinnedApplicationsSection';
import WorkshopStatsSection from './components/WorkshopStatsSection';
import ThemeToggle from './components/ThemeToggle';
import { API_BASE_URL } from '@config';

// Configuration flag for dock panel behavior
// Set to true to keep the dock always visible (recommended for admin dashboard)
// Set to false to enable auto-hide behavior after a few seconds
const DOCK_ALWAYS_VISIBLE = true;

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%',
		minHeight: '100%',
		// Grow to fill the layout's scroll area (and with our own content) so
		// the background covers it on tall viewports; never shrink. The
		// layout's FuseScrollbars handles the actual overflow/scrolling.
		flex: '1 0 auto',
		padding: theme.spacing(3),
		// Reserve scroll space for the fixed dock (PinnedApplicationsSection
		// publishes its footprint); falls back to the base padding when the
		// dock isn't mounted. The section's own trailing margin provides the
		// visual gap above the dock, so the safe area isn't padded further.
		paddingBottom: 'max(24px, var(--dock-safe-area, 0px))',
		boxSizing: 'border-box'
	},
	rootDark: {
		backgroundColor: '#0f172a', // Dark slate background
	},
	rootLight: {
		backgroundColor: '#f1f5f9', // Light slate background
	},
	loadingContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		minHeight: '400px'
	},
	loadingDark: {
		color: '#94a3b8'
	},
	loadingLight: {
		color: '#64748b'
	},
	errorContainer: {
		padding: theme.spacing(3)
	},
	headerSection: {
		marginBottom: theme.spacing(4)
	},
	headerTitle: {
		fontSize: '28px',
		fontWeight: 600,
		marginBottom: theme.spacing(0.5)
	},
	headerTitleDark: {
		color: '#f8fafc'
	},
	headerTitleLight: {
		color: '#0f172a'
	},
	headerSubtitle: {
		fontSize: '14px',
		fontWeight: 400
	},
	headerSubtitleDark: {
		color: '#94a3b8'
	},
	headerSubtitleLight: {
		color: '#64748b'
	},
	sectionSpacing: {
		marginBottom: theme.spacing(3)
	},
	// Card styles for dark theme
	darkCard: {
		backgroundColor: '#1e293b',
		borderRadius: '8px',
		border: '1px solid #334155',
		boxShadow: 'none',
		'&:hover': {
			borderColor: '#475569'
		}
	},
	// Card styles for light theme
	lightCard: {
		backgroundColor: '#ffffff',
		borderRadius: '8px',
		border: '1px solid #e2e8f0',
		boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
		'&:hover': {
			borderColor: '#cbd5e1',
			boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
		}
	},
	// Layout grid
	mainGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr',
		gap: theme.spacing(3),
		width: '100%',
		[theme.breakpoints.up('lg')]: {
			gridTemplateColumns: '1fr 2fr' // 1/3 for cards, 2/3 for map
		}
	},
	cardsColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(3),
		minWidth: 0 // Allow flex item to shrink below content size
	},
	mapColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: theme.spacing(3),
		minWidth: 0 // Allow flex item to shrink below content size
	}
}));

const ModernDashboard = () => {
	const classes = useStyles();
	const dispatch = useDispatch();

	// Redux state with deep equality check to prevent unnecessary re-renders
	const { metrics, loading: metricsLoading, error: metricsError } = useSelector(
		state => state.dashboard.dashboardMetrics,
		(prev, next) => {
			// Custom equality - only re-render if data actually changed
			if (prev.loading !== next.loading) return false;
			if (prev.error !== next.error) return false;
			if (!prev.metrics && !next.metrics) return true;
			if (!prev.metrics || !next.metrics) return false;
			// Deep compare metrics
			return JSON.stringify(prev.metrics) === JSON.stringify(next.metrics);
		}
	);
	const { themeMode, loading: prefsLoading } = useSelector(
		state => state.dashboard.userPreferences
	);

	const [connectionStatus, setConnectionStatus] = useState({
		type: 'websocket',
		connected: false,
		lastUpdate: null
	});

	// Use refs to track previous values and only update if truly changed
	// Must be declared before any early returns
	const prevSummaryRef = useRef();
	const prevClustersRef = useRef();
	const prevWorkshopsRef = useRef();

	// Fetch data on component mount
	useEffect(() => {
		dispatch(getDashboardMetrics());
		dispatch(getUserPreferences());
	}, [dispatch]);

	// Set up Socket.IO for real-time updates
	useEffect(() => {
		const socket = socketIOClient(API_BASE_URL, {
			transports: ['websocket', 'polling', 'flashsocket'],
			query: { token: window.localStorage.getItem('token') }
		});

		socket.on('connect', () => {
			setConnectionStatus({ type: 'websocket', connected: true, lastUpdate: new Date() });
		});

		socket.on('disconnect', () => {
			setConnectionStatus(prev => ({ ...prev, connected: false }));
		});

		socket.on('connect_error', (error) => {
			console.error('[ModernDashboard] ❌ WebSocket CONNECTION ERROR:', error.message);
			console.error('[ModernDashboard] - Error details:', error);
			setConnectionStatus({ type: 'websocket', connected: false, error: error.message });
		});

		// Listen for dashboard metrics updates
		socket.on('dashboard:metrics:update', (data) => {
			setConnectionStatus(prev => ({ ...prev, lastUpdate: new Date() }));
			if (data && data.summary) {
				dispatch(updateDashboardMetricsRealtime(data));
			}
		});

		// Listen for generic change events (used by original dashboard)
		// NOTE: Don't refetch on change events - we already get real-time updates above
		socket.on('change', (data) => {
			// DO NOT call getDashboardMetrics() here - causes loading flash
			// The dashboard:metrics:update event handles real-time updates
		});

		return () => {
			socket.disconnect();
		};
	}, [dispatch]);

	// Memoize props to prevent unnecessary re-renders of child components
	// Must be before early returns to follow hooks rules
	const memoizedSummary = useMemo(() => {
		if (!metrics || !metrics.summary) return null;
		const summaryStr = JSON.stringify(metrics.summary);
		const prevStr = JSON.stringify(prevSummaryRef.current);
		if (summaryStr === prevStr) {
			return prevSummaryRef.current; // Return same reference
		}
		prevSummaryRef.current = metrics.summary;
		return metrics.summary;
	}, [metrics]);
	
	const memoizedClusters = useMemo(() => {
		if (!metrics || !metrics.clusters) return [];
		return metrics.clusters;
	}, [metrics]);
	
	const memoizedWorkshops = useMemo(() => {
		if (!metrics || !metrics.workshops) return [];
		const workshopsStr = JSON.stringify(metrics.workshops);
		const prevStr = JSON.stringify(prevWorkshopsRef.current);
		if (workshopsStr === prevStr) {
			return prevWorkshopsRef.current; // Return same reference
		}
		prevWorkshopsRef.current = metrics.workshops;
		return metrics.workshops;
	}, [metrics]);

	// Loading state
	if (metricsLoading || prefsLoading) {
		const isDark = themeMode === 'dark';
		return (
			<Box className={`${classes.loadingContainer} ${isDark ? classes.loadingDark : classes.loadingLight}`}>
				<CircularProgress size={60} />
				<Typography variant="h6" style={{ marginTop: 20 }}>
					Loading Dashboard...
				</Typography>
			</Box>
		);
	}

	// Error state
	if (metricsError) {
		return (
			<Box className={classes.errorContainer}>
				<Alert severity="error">
					<Typography variant="h6">Failed to load dashboard metrics</Typography>
					<Typography variant="body2">{metricsError}</Typography>
				</Alert>
			</Box>
		);
	}

	// No data state
	if (!metrics) {
		return (
			<Box className={classes.errorContainer}>
				<Alert severity="info">
					<Typography variant="h6">No metrics available</Typography>
					<Typography variant="body2">
						Dashboard metrics are being aggregated. Please check back in a moment.
					</Typography>
				</Alert>
			</Box>
		);
	}

	const isDark = themeMode === 'dark';

	return (
		<div className={`${classes.root} ${isDark ? classes.rootDark : classes.rootLight}`}>
			{/* Main Grid Layout: Metrics (1/3) | Visualization (2/3) */}
			<div className={classes.mainGrid}>
				{/* Left Column: Gauges + Metrics Cards */}
				<div className={classes.cardsColumn}>
					<MetricGauges metrics={memoizedSummary} clusters={memoizedClusters} themeMode={themeMode} />
					<DashboardMetricsCards metrics={memoizedSummary} themeMode={themeMode} />
				</div>

				{/* Right Column: Tabbed Cluster Visualization (Map/List) */}
				<div className={classes.mapColumn}>
					<ClusterVisualization clusters={memoizedClusters} themeMode={themeMode} />
				</div>
			</div>

			{/* Workshop Stats Section - Horizontal Scroll */}
			<WorkshopStatsSection workshops={memoizedWorkshops} themeMode={themeMode} />

			{/* Pinned Applications - macOS Dock Style (Fixed Position) */}
			<PinnedApplicationsSection workshops={metrics.workshops} alwaysVisible={DOCK_ALWAYS_VISIBLE} />
		</div>
	);
};

export default ModernDashboard;